import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Billboard } from "@react-three/drei/core/Billboard";
import { OrbitControls } from "@react-three/drei/core/OrbitControls";
import * as THREE from "three";

/* ═══════════════════════════════════════════════════════════
   TREE OF LIFE - ULTRA REALISTIC Bioluminescent
   Version 5 - Real bark textures, thick organic trunk,
   massive canopy that hides branches completely
   ═══════════════════════════════════════════════════════════ */

const TX = 4.2, TZ = -3.2;
const LOOK_PRESET = {
  camera: { position: [1.0, 2.9, 18.9], fov: 48, near: 0.1, far: 165 },
  controls: {
    target: [TX, 0.6, TZ],
    minDistance: 11.6,
    maxDistance: 29,
    dampingFactor: 0.1,
    rotateSpeed: 0.3,
    zoomSpeed: 0.7,
    panSpeed: 0.45,
    minPolarAngle: 0.74,
    maxPolarAngle: 1.62,
  },
  fog: { color: "#0c1f31", near: 26, far: 102 },
  dpr: [1, 1.75],
};

/* ── PROCEDURAL BARK TEXTURE (natural, earthy and non-neon) ── */
function makeBarkTexture(sz) {
  const c = document.createElement("canvas");
  c.width = sz;
  c.height = sz;
  const ctx = c.getContext("2d");

  ctx.fillStyle = "#3b2618";
  ctx.fillRect(0, 0, sz, sz);

  for (let i = 0; i < 380; i++) {
    const x = Math.random() * sz;
    const y = Math.random() * sz;
    const w = 1 + Math.random() * 3.5;
    const h = 10 + Math.random() * 46;
    const tone = Math.random();
    if (tone < 0.3) ctx.fillStyle = `rgba(66,41,24,${0.35 + Math.random() * 0.35})`;
    else if (tone < 0.65) ctx.fillStyle = `rgba(49,30,18,${0.35 + Math.random() * 0.35})`;
    else ctx.fillStyle = `rgba(86,55,33,${0.22 + Math.random() * 0.28})`;
    ctx.fillRect(x, y, w, h);
  }

  for (let i = 0; i < 80; i++) {
    const x = Math.random() * sz;
    ctx.strokeStyle = `rgba(18,10,6,${0.45 + Math.random() * 0.35})`;
    ctx.lineWidth = 0.8 + Math.random() * 1.6;
    ctx.beginPath();
    ctx.moveTo(x, Math.random() * sz);
    let cy = Math.random() * sz;
    for (let j = 0; j < 6; j++) {
      cy += 10 + Math.random() * 28;
      ctx.lineTo(x + (Math.random() - 0.5) * 9, cy);
    }
    ctx.stroke();
  }

  for (let i = 0; i < 28; i++) {
    const x = Math.random() * sz;
    const y = Math.random() * sz;
    const r = 5 + Math.random() * 14;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(32,68,38,${0.18 + Math.random() * 0.14})`);
    g.addColorStop(1, "rgba(18,40,24,0)");
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  const t = new THREE.CanvasTexture(c);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.needsUpdate = true;
  return t;
}

/* ── BUMP MAP for bark depth ── */
function makeBarkBump(sz) {
  const c = document.createElement("canvas");
  c.width = sz; c.height = sz;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#808080";
  ctx.fillRect(0, 0, sz, sz);

  // Vertical groove pattern
  for (let i = 0; i < 200; i++) {
    const x = Math.random() * sz;
    const y = Math.random() * sz;
    const w = 1 + Math.random() * 3;
    const h = 10 + Math.random() * 50;
    const v = Math.random() < 0.5 ? 40 + Math.random() * 40 : 160 + Math.random() * 60;
    ctx.fillStyle = `rgb(${v},${v},${v})`;
    ctx.fillRect(x, y, w, h);
  }

  // Deep cracks
  for (let i = 0; i < 40; i++) {
    const x = Math.random() * sz;
    ctx.strokeStyle = `rgb(${20 + Math.random() * 30},${20 + Math.random() * 30},${20 + Math.random() * 30})`;
    ctx.lineWidth = 0.5 + Math.random() * 2;
    ctx.beginPath();
    ctx.moveTo(x, Math.random() * sz);
    let cy = Math.random() * sz;
    for (let j = 0; j < 5; j++) { cy += 15 + Math.random() * 25; ctx.lineTo(x + (Math.random() - 0.5) * 6, cy); }
    ctx.stroke();
  }

  const t = new THREE.CanvasTexture(c);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.needsUpdate = true;
  return t;
}

/* ── FOLIAGE cluster texture ── */
function makeFoliageTex(sz) {
  const c = document.createElement("canvas");
  c.width = sz;
  c.height = sz;
  const ctx = c.getContext("2d");
  const h = sz / 2;

  for (let i = 0; i < 420; i++) {
    const angle = Math.random() * Math.PI * 2;
    const maxR = h * (0.56 + Math.abs(Math.sin(angle * 2.3 + Math.random() * 0.9)) * 0.46);
    const dist = Math.pow(Math.random(), 0.63) * maxR;
    const x = h + Math.cos(angle) * dist;
    const y = h + Math.sin(angle) * dist;
    const len = 8 + Math.random() * (sz * 0.052);
    const wid = 3 + Math.random() * (sz * 0.022);
    const rot = Math.random() * Math.PI;
    const ch = Math.random();
    const br = 0.84 + Math.random() * 0.24;
    let cr, cg, cb;
    if (ch < 0.26) { cr = 112 * br; cg = 162 * br; cb = 78 * br; }
    else if (ch < 0.56) { cr = 94 * br; cg = 145 * br; cb = 64 * br; }
    else if (ch < 0.84) { cr = 80 * br; cg = 128 * br; cb = 56 * br; }
    else { cr = 132 * br; cg = 177 * br; cb = 95 * br; }
    const fade = 1 - dist / (maxR + 0.001);
    const alpha = (0.34 + Math.random() * 0.34) * fade;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rot);
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, len);
    g.addColorStop(0, `rgba(${cr | 0},${cg | 0},${cb | 0},${alpha})`);
    g.addColorStop(0.6, `rgba(${(cr * 0.8) | 0},${(cg * 0.82) | 0},${(cb * 0.78) | 0},${alpha * 0.7})`);
    g.addColorStop(1, `rgba(${(cr * 0.42) | 0},${(cg * 0.46) | 0},${(cb * 0.42) | 0},0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(0, 0, len, wid, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = "rgba(34,52,22,0.03)";
  ctx.fillRect(0, 0, sz, sz);

  const ef = ctx.createRadialGradient(h, h, h * 0.6, h, h, h);
  ef.addColorStop(0, "rgba(0,0,0,0)");
  ef.addColorStop(0.82, "rgba(0,0,0,0)");
  ef.addColorStop(1, "rgba(0,0,0,0.42)");
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = ef;
  ctx.fillRect(0, 0, sz, sz);

  const t = new THREE.CanvasTexture(c);
  t.needsUpdate = true;
  return t;
}

/* ── FLOWER texture ── */
function makeFlowerTex(sz) {
  const c = document.createElement("canvas");
  c.width = sz; c.height = sz;
  const ctx = c.getContext("2d"), h = sz / 2;
  for (let i = 0; i < 100; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.pow(Math.random(), 0.5) * h * 0.82;
    const x = h + Math.cos(angle) * dist;
    const y = h + Math.sin(angle) * dist;
    const r = 6 + Math.random() * (sz * 0.09);
    const fade = 1 - dist / (h * 0.92);
    const ch = Math.random();
    let cr, cg, cb;
    if (ch < 0.3) { cr = 255; cg = 55; cb = 225; }
    else if (ch < 0.5) { cr = 255; cg = 85; cb = 185; }
    else if (ch < 0.7) { cr = 225; cg = 45; cb = 255; }
    else if (ch < 0.85) { cr = 255; cg = 120; cb = 200; }
    else { cr = 200; cg = 30; cb = 180; }
    const a = fade * (0.55 + Math.random() * 0.45);
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(${cr},${cg},${cb},${a})`);
    g.addColorStop(0.45, `rgba(${cr * 0.7 | 0},${cg * 0.5 | 0},${cb * 0.7 | 0},${a * 0.55})`);
    g.addColorStop(1, `rgba(${cr * 0.3 | 0},${cg * 0.2 | 0},${cb * 0.3 | 0},0)`);
    ctx.fillStyle = g; ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  const ef = ctx.createRadialGradient(h, h, h * 0.48, h, h, h);
  ef.addColorStop(0, "rgba(0,0,0,0)"); ef.addColorStop(0.68, "rgba(0,0,0,0)"); ef.addColorStop(1, "rgba(0,0,0,1)");
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = ef; ctx.fillRect(0, 0, sz, sz);
  const t = new THREE.CanvasTexture(c); t.needsUpdate = true; return t;
}

/* ── BRAIN FRUIT textures (small brain-like fruits) ── */
function makeBrainTex(sz) {
  const c = document.createElement("canvas");
  c.width = sz; c.height = sz;
  const ctx = c.getContext("2d");
  const h = sz / 2;

  // Base tone (realistic brain-like palette)
  const base = ctx.createRadialGradient(h, h, sz * 0.05, h, h, h);
  base.addColorStop(0, "#ffe6e1");
  base.addColorStop(0.54, "#f0b7af");
  base.addColorStop(1, "#bf7a7f");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, sz, sz);

  // Gyri folds
  for (let i = 0; i < 150; i++) {
    const side = Math.random() < 0.5 ? -1 : 1;
    const x = h + side * (Math.random() * h * 0.48);
    const y = h + (Math.random() - 0.5) * h * 1.1;
    const len = 6 + Math.random() * 20;
    ctx.strokeStyle =
      Math.random() < 0.62
        ? `rgba(123,58,70,${0.38 + Math.random() * 0.28})`
        : `rgba(253,225,219,${0.18 + Math.random() * 0.22})`;
    ctx.lineWidth = 1.2 + Math.random() * 2.2;
    ctx.beginPath();
    ctx.moveTo(x - len, y);
    ctx.quadraticCurveTo(
      x,
      y + (Math.random() - 0.5) * 8,
      x + len,
      y + (Math.random() - 0.5) * 4
    );
    ctx.stroke();
  }

  // Mid split
  ctx.strokeStyle = "rgba(250,230,225,0.45)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(h, h - h * 0.75);
  ctx.quadraticCurveTo(h + 4, h, h, h + h * 0.75);
  ctx.stroke();

  // Edge fade
  const edge = ctx.createRadialGradient(h, h, h * 0.55, h, h, h);
  edge.addColorStop(0, "rgba(0,0,0,0)");
  edge.addColorStop(1, "rgba(0,0,0,0.5)");
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = edge;
  ctx.fillRect(0, 0, sz, sz);

  const t = new THREE.CanvasTexture(c);
  t.needsUpdate = true;
  return t;
}

function makeBrainBump(sz) {
  const c = document.createElement("canvas");
  c.width = sz; c.height = sz;
  const ctx = c.getContext("2d");
  const h = sz / 2;

  ctx.fillStyle = "#989898";
  ctx.fillRect(0, 0, sz, sz);

  for (let i = 0; i < 180; i++) {
    const side = Math.random() < 0.5 ? -1 : 1;
    const x = h + side * (Math.random() * h * 0.5);
    const y = h + (Math.random() - 0.5) * h * 1.2;
    const len = 5 + Math.random() * 18;
    const value = Math.random() < 0.5 ? 55 + Math.random() * 35 : 160 + Math.random() * 40;
    ctx.strokeStyle = `rgb(${value},${value},${value})`;
    ctx.lineWidth = 1 + Math.random() * 1.8;
    ctx.beginPath();
    ctx.moveTo(x - len, y);
    ctx.quadraticCurveTo(
      x,
      y + (Math.random() - 0.5) * 8,
      x + len,
      y + (Math.random() - 0.5) * 4
    );
    ctx.stroke();
  }

  ctx.strokeStyle = "rgb(70,70,70)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(h, h - h * 0.8);
  ctx.lineTo(h, h + h * 0.8);
  ctx.stroke();

  const t = new THREE.CanvasTexture(c);
  t.needsUpdate = true;
  return t;
}

/* ── GLOW sprite ── */
function makeGlow(sz, c0, c1, c2) {
  const c = document.createElement("canvas");
  c.width = sz; c.height = sz;
  const ctx = c.getContext("2d"), h = sz / 2;
  const g = ctx.createRadialGradient(h, h, 0, h, h, h);
  g.addColorStop(0, c0 || "rgba(255,255,255,1)");
  g.addColorStop(0.15, c1 || "rgba(255,255,255,0.8)");
  g.addColorStop(0.5, c2 || "rgba(255,255,255,0.15)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, sz, sz);
  const t = new THREE.CanvasTexture(c); t.needsUpdate = true; return t;
}

function makeBrainFruitGeometry() {
  const g = new THREE.SphereGeometry(1, 24, 18);
  const p = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i);
    const ang = Math.atan2(v.z, v.x);
    const groove = Math.exp(-Math.pow(v.x * 1.8, 2)) * 0.13;
    const gyri = Math.sin(v.y * 12 + ang * 3.2) * 0.045 + Math.cos(v.z * 11 - ang * 2.4) * 0.03;
    const scale = 1 + gyri - groove;
    v.x *= scale * 1.08;
    v.y *= scale * 0.84;
    v.z *= scale;
    p.setXYZ(i, v.x, v.y, v.z);
  }
  p.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

function makeOrganicTrunkGeometry() {
  const g = new THREE.CylinderGeometry(1.08, 0.56, 8.8, 24, 48, false);
  const p = g.attributes.position;
  const v = new THREE.Vector3();
  let invalidVertexCount = 0;

  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i);
    const yNorm = (v.y + 4.4) / 8.8;
    const ang = Math.atan2(v.z, v.x);

    const baseFlare = Math.pow(1 - yNorm, 2.2) * 0.8;
    const buttress = Math.max(0, 1 - yNorm * 1.9) * Math.pow(Math.max(0, Math.cos(ang * 3.4 + 0.6)), 3.4) * 0.6;
    const barkNoise = Math.sin(ang * 7.2 + yNorm * 16.0) * 0.05 + Math.cos(ang * 4.5 - yNorm * 14.0) * 0.04;
    const taperWobble = Math.sin(yNorm * 5.0 + ang * 0.8) * 0.05;
    const scale = 1 + baseFlare + buttress + barkNoise + taperWobble;

    const nx = v.x * scale + Math.sin(yNorm * 6.2 + ang) * 0.055;
    const nz = v.z * scale * (0.95 + Math.sin(yNorm * 3.7) * 0.05) + Math.cos(yNorm * 5.5 - ang * 0.7) * 0.05;

    if (!Number.isFinite(nx) || !Number.isFinite(v.y) || !Number.isFinite(nz)) {
      invalidVertexCount += 1;
      p.setXYZ(i, 0, v.y || 0, 0);
      continue;
    }

    p.setXYZ(i, nx, v.y, nz);
  }

  p.needsUpdate = true;
  g.computeVertexNormals();
  g.computeBoundingSphere();

  // Hard guard against invalid geometry that can crash WebGL context.
  if (invalidVertexCount > 0 || !g.boundingSphere || !Number.isFinite(g.boundingSphere.radius)) {
    const fallback = new THREE.CylinderGeometry(1.08, 0.56, 8.8, 24, 48, false);
    fallback.computeVertexNormals();
    fallback.computeBoundingSphere();
    return fallback;
  }

  return g;
}

// Pre-create textures
const barkTex = makeBarkTexture(1024);
const barkBump = makeBarkBump(1024);
const foliageTex = makeFoliageTex(1024);
const flowerTex = makeFlowerTex(256);
const brainTex = makeBrainTex(512);
const brainBump = makeBrainBump(512);
const brainFruitGeo = makeBrainFruitGeometry();
const glowSprite = makeGlow(64);
const bigGlow = makeGlow(128, "rgba(255,255,255,1)", "rgba(255,255,255,0.5)", "rgba(255,255,255,0.08)");

/* ═══════════════════════════════════════════════════════════
   REALISTIC TREE TRUNK - thick, organic, with real bark
   ═══════════════════════════════════════════════════════════ */
function TreeTrunk() {
  const groupRef = useRef(null);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (groupRef.current) {
      groupRef.current.rotation.z = Math.sin(t * 0.11) * 0.008;
      groupRef.current.rotation.x = Math.sin(t * 0.09 + 0.4) * 0.006;
    }
  });

  const { trunkGeo, branchGeos, rootGeos } = useMemo(() => {
    const _trunkGeo = makeOrganicTrunkGeometry();

    const branchDefs = [
      { pts: [[0.18, 0.95, 0.0], [1.2, 1.9, 0.4], [2.6, 2.9, 0.95], [4.1, 3.8, 1.35]], r: 0.28 },
      { pts: [[0.15, 1.2, -0.15], [1.0, 2.0, -0.7], [2.2, 3.05, -1.05], [3.2, 3.9, -1.3]], r: 0.25 },
      { pts: [[0.1, 1.45, 0.08], [0.6, 2.7, 0.35], [0.7, 3.8, 0.3], [0.55, 4.75, 0.1]], r: 0.2 },
      { pts: [[-0.22, 0.9, -0.1], [-1.45, 1.95, -0.45], [-2.8, 2.95, -0.95], [-4.0, 3.75, -1.35]], r: 0.27 },
      { pts: [[-0.12, 1.25, 0.15], [-1.2, 2.2, 0.6], [-2.45, 3.2, 1.1], [-3.45, 4.05, 1.5]], r: 0.24 },
      { pts: [[-0.04, 1.6, -0.1], [0.22, 2.85, -0.5], [0.0, 3.9, -0.95], [-0.25, 4.7, -1.3]], r: 0.19 },
      { pts: [[2.6, 2.9, 0.95], [3.3, 3.5, 1.35], [3.9, 3.95, 1.7]], r: 0.095 },
      { pts: [[2.2, 3.05, -1.05], [2.8, 3.7, -1.35], [3.25, 4.05, -1.7]], r: 0.09 },
      { pts: [[-2.8, 2.95, -0.95], [-3.35, 3.65, -1.2], [-3.85, 4.05, -1.55]], r: 0.095 },
      { pts: [[-2.45, 3.2, 1.1], [-2.95, 3.8, 1.55], [-3.35, 4.2, 1.95]], r: 0.09 },
      { pts: [[0.7, 3.8, 0.3], [1.0, 4.45, 0.65], [1.2, 4.9, 0.95]], r: 0.075 },
      { pts: [[0.0, 3.9, -0.95], [-0.2, 4.5, -1.35], [-0.45, 4.95, -1.65]], r: 0.075 },
    ];
    const _branchGeos = branchDefs.map((def) => {
      const curve = new THREE.CatmullRomCurve3(def.pts.map(p => new THREE.Vector3(...p)));
      return new THREE.TubeGeometry(curve, 18, def.r, 12, false);
    });

    const _rootGeos = [];
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2 + (Math.random() - 0.5) * 0.26;
      const len = 2.6 + Math.random() * 3.3;
      const lift = 0.18 + Math.random() * 0.2;
      const rootPts = [
        new THREE.Vector3(Math.cos(a) * 0.45, -3.95 + lift, Math.sin(a) * 0.45),
        new THREE.Vector3(Math.cos(a) * 1.15, -4.2 + lift, Math.sin(a) * 1.15),
        new THREE.Vector3(Math.cos(a) * 2.25, -4.55 + Math.random() * 0.16, Math.sin(a) * 2.25),
        new THREE.Vector3(Math.cos(a) * len, -5.02 + Math.random() * 0.14, Math.sin(a) * len),
      ];
      const rootCurve = new THREE.CatmullRomCurve3(rootPts);
      _rootGeos.push(new THREE.TubeGeometry(rootCurve, 14, 0.12 + Math.random() * 0.11, 10, false));
    }

    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
      const out = 1.0 + Math.random() * 1.6;
      const buttressPts = [
        new THREE.Vector3(Math.cos(a) * 0.24, -3.45, Math.sin(a) * 0.24),
        new THREE.Vector3(Math.cos(a) * 0.75, -3.0, Math.sin(a) * 0.75),
        new THREE.Vector3(Math.cos(a) * 1.3, -3.5, Math.sin(a) * 1.3),
        new THREE.Vector3(Math.cos(a) * out, -4.2, Math.sin(a) * out),
      ];
      const buttressCurve = new THREE.CatmullRomCurve3(buttressPts);
      _rootGeos.push(new THREE.TubeGeometry(buttressCurve, 12, 0.14 + Math.random() * 0.08, 10, false));
    }

    return { trunkGeo: _trunkGeo, branchGeos: _branchGeos, rootGeos: _rootGeos };
  }, []);

  useEffect(() => () => {
    trunkGeo.dispose();
    branchGeos.forEach(g => g.dispose());
    rootGeos.forEach(g => g.dispose());
  }, [trunkGeo, branchGeos, rootGeos]);

  const barkMat = useMemo(() => ({
    map: barkTex,
    bumpMap: barkBump,
    bumpScale: 0.58,
    roughness: 0.86,
    metalness: 0.02,
    color: "#7b5433",
    emissive: "#1a0f07",
    emissiveIntensity: 0.04,
  }), []);
  const branchMat = useMemo(() => ({
    map: barkTex,
    bumpMap: barkBump,
    bumpScale: 0.56,
    roughness: 0.84,
    metalness: 0.02,
    color: "#88623f",
    emissive: "#1b1008",
    emissiveIntensity: 0.045,
  }), []);
  const rootMat = useMemo(() => ({
    map: barkTex,
    bumpMap: barkBump,
    bumpScale: 0.6,
    roughness: 0.9,
    metalness: 0.01,
    color: "#8d6844",
    emissive: "#1c1109",
    emissiveIntensity: 0.05,
  }), []);

  return (
    <group ref={groupRef} position={[TX, -0.2, TZ]}>
      <mesh geometry={trunkGeo} castShadow receiveShadow>
        <meshStandardMaterial {...barkMat} />
      </mesh>
      {branchGeos.map((geo, i) => (
        <mesh key={`br-${i}`} geometry={geo} castShadow receiveShadow>
          <meshStandardMaterial {...branchMat} />
        </mesh>
      ))}
      {rootGeos.map((geo, i) => (
        <mesh key={`rt-${i}`} geometry={geo} castShadow receiveShadow>
          <meshStandardMaterial {...rootMat} />
        </mesh>
      ))}
    </group>
  );
}

/* ═══════════════════════════════════════════════════════════
   MASSIVE CANOPY - completely covers branches with dense foliage
   ═══════════════════════════════════════════════════════════ */
function MassiveCanopy() {
  const groupRef = useRef(null);
  const leafRefs = useRef([]);
  const canopyLeaves = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 980; i++) {
      const h = Math.random();
      const y = 0.9 + h * 4.9;
      const spread = Math.max(0.35, 1 - Math.pow((h - 0.6) / 0.72, 2)) * 4.95;
      const a = Math.random() * Math.PI * 2;
      let r = Math.pow(Math.random(), 0.58) * spread;
      if (y < 2.65) r += 1.15;
      if (h < 0.22) r += 0.42;
      arr.push({
        base: [TX + Math.cos(a) * r * 1.12, y, TZ + Math.sin(a) * r * 1.03],
        size: 0.42 + Math.random() * 0.78 + (1 - h) * 0.2,
        phase: Math.random() * Math.PI * 2,
        colorIndex: Math.floor(Math.random() * 5),
        lift: 0.02 + Math.random() * 0.06,
      });
    }
    return arr;
  }, []);

  const canopyPalette = useMemo(() => ["#6c9a53", "#5f8b48", "#7ba95e", "#87b367", "#4f7a3e"], []);
  const leafMaterials = useMemo(
    () =>
      canopyPalette.map(
        (color) =>
          new THREE.MeshStandardMaterial({
            map: foliageTex,
            transparent: true,
            alphaTest: 0.015,
            depthWrite: false,
            side: THREE.DoubleSide,
            opacity: 0.96,
            color,
            roughness: 0.86,
            metalness: 0.0,
            emissive: "#213a19",
            emissiveIntensity: 0.07,
          })
      ),
    [canopyPalette]
  );

  useEffect(
    () => () => {
      leafMaterials.forEach((m) => m.dispose());
    },
    [leafMaterials]
  );

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const windX = Math.sin(t * 0.2) * 0.07 + Math.sin(t * 0.44) * 0.028;
    const windZ = Math.cos(t * 0.15) * 0.06;
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(t * 0.1) * 0.016;
    }
    leafRefs.current.forEach((ref, i) => {
      const cl = canopyLeaves[i];
      if (!ref || !cl) return;
      const hf = THREE.MathUtils.clamp((cl.base[1] - 1.1) * 0.16, 0.2, 1.1);
      ref.position.x = cl.base[0] + (windX + Math.sin(t * 0.28 + cl.phase) * 0.03) * hf;
      ref.position.y = cl.base[1] + Math.sin(t * 0.42 + cl.phase * 1.16) * cl.lift;
      ref.position.z = cl.base[2] + (windZ + Math.cos(t * 0.24 + cl.phase) * 0.03) * hf;
      const pulse = 1 + Math.sin(t * 0.38 + cl.phase) * 0.03;
      ref.scale.set(cl.size * 1.06 * pulse, cl.size * 0.72 * pulse, 1);
    });
  });

  return (
    <group ref={groupRef}>
      {canopyLeaves.map((cl, i) => (
        <Billboard
          key={`leaf-card-${i}`}
          ref={(el) => {
            leafRefs.current[i] = el;
          }}
          position={cl.base}
          follow={true}
        >
          <mesh material={leafMaterials[cl.colorIndex]} castShadow={false} receiveShadow={false}>
            <planeGeometry args={[1.34, 1]} />
          </mesh>
        </Billboard>
      ))}
    </group>
  );
}

/* ─── Texture tuning for sharper branches and fruits ─── */
function RenderQualityTuner() {
  const { gl } = useThree();

  useEffect(() => {
    const maxAnisotropy = gl.capabilities.getMaxAnisotropy?.() || 1;
    const targetAnisotropy = Math.max(1, Math.min(12, maxAnisotropy));
    [barkTex, barkBump, foliageTex, brainTex, brainBump].forEach((texture) => {
      texture.anisotropy = targetAnisotropy;
      texture.needsUpdate = true;
    });
  }, [gl]);

  return null;
}

/* ─── Brain-like fruits reacting to zoom ─── */
function BrainFruits() {
  const fruitRefs = useRef([]);
  const { camera } = useThree();
  const target = useMemo(() => new THREE.Vector3(TX, 2.8, TZ), []);

  const fruits = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 24; i++) {
      const h = 0.18 + Math.random() * 0.74;
      const y = 0.8 + h * 5.3;
      const spread = Math.sin(h * Math.PI) * 3.8;
      const a = Math.random() * Math.PI * 2;
      const r = (0.35 + Math.random() * 0.65) * spread;
      const scale = 0.16 + Math.random() * 0.11;
      arr.push({
        p: [Math.cos(a) * r + TX, y, Math.sin(a) * r + TZ],
        phase: Math.random() * Math.PI * 2,
        spin: 0.7 + Math.random() * 1.1,
        scale,
      });
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const dist = camera.position.distanceTo(target);
    const zoomIntensity = THREE.MathUtils.clamp((18 - dist) / 12, 0, 1);

    fruitRefs.current.forEach((mesh, i) => {
      if (!mesh || !fruits[i]) return;
      const fruit = fruits[i];
      mesh.position.x = fruit.p[0] + Math.sin(t * 0.35 + fruit.phase) * 0.05;
      mesh.position.y = fruit.p[1] + Math.cos(t * 0.42 + fruit.phase) * 0.05;
      mesh.position.z = fruit.p[2] + Math.sin(t * 0.28 + fruit.phase * 0.7) * 0.04;
      mesh.rotation.y += 0.002 * fruit.spin;
      mesh.rotation.z = Math.sin(t * 0.6 + fruit.phase) * 0.14;

      const pulse = 0.92 + Math.sin(t * 1.05 + fruit.phase) * 0.07;
      const zoomScale = 1 + zoomIntensity * 0.28;
      const s = fruit.scale * pulse * zoomScale;
      mesh.scale.set(s * 1.08, s * 0.86, s);

      if (mesh.material) {
        mesh.material.emissiveIntensity = 0.08 + zoomIntensity * 0.1 + Math.sin(t * 1.25 + fruit.phase) * 0.018;
      }
    });
  });

  return (
    <group>
      {fruits.map((fruit, i) => (
        <mesh key={`brain-fruit-${i}`} ref={(el) => { fruitRefs.current[i] = el; }} position={fruit.p} castShadow={false} geometry={brainFruitGeo}>
          <meshPhysicalMaterial
            map={brainTex}
            bumpMap={brainBump}
            bumpScale={0.21}
            roughness={0.54}
            metalness={0.01}
            clearcoat={0.38}
            clearcoatRoughness={0.36}
            transmission={0.01}
            ior={1.22}
            emissive="#7b4d52"
            emissiveIntensity={0.11}
          />
        </mesh>
      ))}
    </group>
  );
}

/* ─── SKY ATMOSPHERE ─── */
function SkyAtmosphere() {
  return (
    <group>
      <mesh position={[0, 12, -8]}>
        <sphereGeometry args={[88, 36, 28]} />
        <meshBasicMaterial color="#0f2740" side={THREE.BackSide} />
      </mesh>
      <mesh position={[0, 20, -12]}>
        <sphereGeometry args={[76, 32, 24]} />
        <meshBasicMaterial color="#1f3d59" transparent opacity={0.18} side={THREE.BackSide} />
      </mesh>
    </group>
  );
}

function DistantMountains() {
  const ridges = useMemo(
    () => [
      { p: [-12, -2.7, -27], s: [12, 7.2, 5.5], r: [0.02, 0.3, -0.04], c: "#27455e", o: 0.2 },
      { p: [-4, -2.4, -25], s: [10.5, 6.6, 5.2], r: [0.03, -0.1, 0.02], c: "#2d5270", o: 0.18 },
      { p: [4.5, -2.2, -23], s: [10, 6.2, 5], r: [0.02, 0.18, -0.03], c: "#335f7a", o: 0.15 },
      { p: [12, -2.6, -26], s: [11.8, 6.8, 5.4], r: [0.02, -0.24, 0.03], c: "#2b4c67", o: 0.19 },
      { p: [0.8, -2.9, -31], s: [18, 8.2, 6.8], r: [0.02, 0.05, 0], c: "#25415a", o: 0.16 },
    ],
    []
  );

  return (
    <group>
      {ridges.map((ridge, i) => (
        <mesh key={`ridge-${i}`} position={ridge.p} scale={ridge.s} rotation={ridge.r}>
          <icosahedronGeometry args={[1, 1]} />
          <meshStandardMaterial color={ridge.c} roughness={1} metalness={0} transparent opacity={ridge.o} depthWrite={false} />
        </mesh>
      ))}
      <mesh position={[0.2, -3.05, -31.4]} scale={[24, 3.4, 1]} rotation={[-0.04, 0.02, 0]}>
        <circleGeometry args={[1, 80]} />
        <meshBasicMaterial color="#86b9ca" transparent opacity={0.09} depthWrite={false} />
      </mesh>
    </group>
  );
}

/* ─── Flower Bushes (textured billboards) ─── */
function FlowerBushes() {
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    refs.current.forEach((f, i) => { if (f) f.material.opacity = 0.75 + Math.sin(t * 0.5 + i * 0.6) * 0.15; });
  });
  const bushes = useMemo(() => {
    const res = [];
    for (let i = 0; i < 80; i++) {
      const a = Math.random() * Math.PI * 2;
      const cl = Math.random();
      const r = cl < 0.3 ? (1.2 + Math.random() * 2.5) : cl < 0.6 ? (3 + Math.random() * 3.5) : (5 + Math.random() * 4.5);
      res.push({ p: [Math.cos(a) * r + TX, -4.5 + Math.random() * 0.8, Math.sin(a) * r + TZ], s: 0.6 + Math.random() * 1.2 });
    }
    return res;
  }, []);
  return (
    <group>
      {bushes.map((b, i) => (
        <Billboard key={i} position={b.p}>
          <mesh ref={(el) => { refs.current[i] = el; }}>
            <planeGeometry args={[b.s, b.s * 0.75]} />
            <meshBasicMaterial map={flowerTex} transparent opacity={0.8} blending={THREE.AdditiveBlending} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
        </Billboard>
      ))}
    </group>
  );
}

/* ─── Flower Particles ─── */
function FlowerParticles() {
  const { geo, ph, bp } = useMemo(() => {
    const c = 1500, pos = new Float32Array(c * 3), col = new Float32Array(c * 3);
    const _ph = new Float32Array(c), _bp = new Float32Array(c * 3);
    for (let i = 0; i < c; i++) {
      const i3 = i * 3, a = Math.random() * Math.PI * 2, r = 1.2 + Math.random() * 8;
      pos[i3] = Math.cos(a) * r + TX; pos[i3 + 1] = -5.0 + Math.random() * 1.8; pos[i3 + 2] = Math.sin(a) * r + TZ;
      _bp[i3] = pos[i3]; _bp[i3 + 1] = pos[i3 + 1]; _bp[i3 + 2] = pos[i3 + 2];
      const ch = Math.random(), br = 0.8 + Math.random() * 0.2;
      if (ch < 0.3) { col[i3] = 1.0 * br; col[i3 + 1] = 0.12 * br; col[i3 + 2] = 0.9 * br; }
      else if (ch < 0.55) { col[i3] = 1.0 * br; col[i3 + 1] = 0.25 * br; col[i3 + 2] = 0.7 * br; }
      else if (ch < 0.75) { col[i3] = 0.85 * br; col[i3 + 1] = 0.35 * br; col[i3 + 2] = 1.0 * br; }
      else { col[i3] = 0.7 * br; col[i3 + 1] = 0.08 * br; col[i3 + 2] = 0.85 * br; }
      _ph[i] = Math.random() * Math.PI * 2;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    return { geo: g, ph: _ph, bp: _bp };
  }, []);
  useEffect(() => () => geo.dispose(), [geo]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime(), arr = geo.attributes.position.array;
    for (let i = 0; i < 1500; i++) {
      const i3 = i * 3;
      arr[i3] = bp[i3] + Math.sin(t * 0.3 + ph[i]) * 0.02;
      arr[i3 + 1] = bp[i3 + 1] + Math.sin(t * 0.5 + ph[i]) * 0.01;
    }
    geo.attributes.position.needsUpdate = true;
  });
  return (
    <points geometry={geo}>
      <pointsMaterial map={glowSprite} size={0.25} sizeAttenuation transparent opacity={0.85} depthWrite={false} blending={THREE.AdditiveBlending} vertexColors />
    </points>
  );
}

/* ─── Fireflies ─── */
function Fireflies() {
  const { geo, ph, bp } = useMemo(() => {
    const c = 150, pos = new Float32Array(c * 3), col = new Float32Array(c * 3);
    const _ph = new Float32Array(c), _bp = new Float32Array(c * 3);
    for (let i = 0; i < c; i++) {
      const i3 = i * 3;
      pos[i3] = (Math.random() - 0.5) * 24 + TX;
      pos[i3 + 1] = -4 + Math.random() * 13;
      pos[i3 + 2] = (Math.random() - 0.5) * 18 + TZ;
      _bp[i3] = pos[i3]; _bp[i3 + 1] = pos[i3 + 1]; _bp[i3 + 2] = pos[i3 + 2];
      _ph[i] = Math.random() * Math.PI * 2;
      const ch = Math.random();
      if (ch < 0.45) { col[i3] = 1.0; col[i3 + 1] = 0.85; col[i3 + 2] = 0.3; }
      else if (ch < 0.75) { col[i3] = 0.4; col[i3 + 1] = 1.0; col[i3 + 2] = 0.7; }
      else { col[i3] = 1.0; col[i3 + 1] = 0.7; col[i3 + 2] = 0.2; }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    return { geo: g, ph: _ph, bp: _bp };
  }, []);
  useEffect(() => () => geo.dispose(), [geo]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime(), arr = geo.attributes.position.array;
    for (let i = 0; i < 150; i++) {
      const i3 = i * 3, p = ph[i];
      arr[i3] = bp[i3] + Math.sin(t * 0.12 + p) * 1.8 + Math.sin(t * 0.4 + p * 2.1) * 0.5;
      arr[i3 + 1] = bp[i3 + 1] + Math.sin(t * 0.3 + p) * 2.0 + Math.cos(t * 0.55 + p * 1.3) * 0.6;
      arr[i3 + 2] = bp[i3 + 2] + Math.cos(t * 0.11 + p) * 1.5;
    }
    geo.attributes.position.needsUpdate = true;
  });
  return (
    <points geometry={geo}>
      <pointsMaterial map={glowSprite} size={0.45} sizeAttenuation transparent opacity={0.88} depthWrite={false} blending={THREE.AdditiveBlending} vertexColors />
    </points>
  );
}

/* ─── Background forest silhouettes ─── */
function BackgroundForest() {
  const trees = useMemo(() => {
    const r = [];
    for (let i = 0; i < 22; i++) {
      const x = (i - 11) * 2.5 + (Math.random() - 0.5) * 2;
      const z = -16 - Math.random() * 18;
      const h = 6 + Math.random() * 10;
      r.push({
        p: [x, -5 + h / 2, z],
        s: [0.2 + Math.random() * 0.2, h, 0.2 + Math.random() * 0.2],
        canopy: [x, -5 + h * 0.6, z],
        cr: 1.8 + Math.random() * 2.6,
        d: Math.abs(z) / 22,
      });
    }
    return r;
  }, []);
  return (
    <group>
      {trees.map((t, i) => (
        <React.Fragment key={i}>
          <mesh position={t.p} scale={t.s}>
            <cylinderGeometry args={[0.5, 1, 1, 6]} />
            <meshBasicMaterial color="#040a0f" transparent opacity={0.22 - t.d * 0.06} />
          </mesh>
          <mesh position={t.canopy}>
            <sphereGeometry args={[t.cr, 14, 12]} />
            <meshBasicMaterial color="#061018" transparent opacity={0.14 - t.d * 0.04} />
          </mesh>
        </React.Fragment>
      ))}
    </group>
  );
}

/* ─── Water surface ─── */
function TreeIsland() {
  const moundGeo = useMemo(() => {
    const g = new THREE.SphereGeometry(1, 72, 44);
    const p = g.attributes.position;
    const v = new THREE.Vector3();
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i);
      const radial = Math.sqrt(v.x * v.x + v.z * v.z);
      const rim = THREE.MathUtils.smoothstep(radial, 0.52, 1.04);
      const und = Math.sin(v.x * 7.2) * 0.025 + Math.cos(v.z * 6.4) * 0.022 + Math.sin((v.x + v.z) * 4.3) * 0.018;
      v.y = v.y * 0.58 - rim * 0.3 + und;
      p.setXYZ(i, v.x, v.y, v.z);
    }
    p.needsUpdate = true;
    g.computeVertexNormals();
    return g;
  }, []);

  useEffect(() => () => moundGeo.dispose(), [moundGeo]);

  return (
    <group>
      <mesh position={[TX, -5.28, TZ]} scale={[7.1, 1.95, 5.55]} receiveShadow castShadow geometry={moundGeo}>
        <meshStandardMaterial color="#6d593f" roughness={0.98} metalness={0.0} />
      </mesh>
      <mesh position={[TX + 0.18, -4.84, TZ - 0.15]} scale={[5.1, 0.7, 3.9]} receiveShadow>
        <sphereGeometry args={[1, 48, 28]} />
        <meshStandardMaterial color="#7d6647" roughness={0.97} metalness={0.0} />
      </mesh>
    </group>
  );
}

/* ─── Water surface ─── */
function Water() {
  const wRef = useRef(null), gRef = useRef(null);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (wRef.current) wRef.current.material.opacity = 0.23 + Math.sin(t * 0.08) * 0.02;
    if (gRef.current) gRef.current.material.opacity = 0.11 + Math.sin(t * 0.14) * 0.02;
  });
  return (
    <group>
      <mesh ref={wRef} rotation={[-Math.PI / 2, 0, 0]} position={[TX, -5.34, TZ - 0.9]}>
        <circleGeometry args={[30, 96]} />
        <meshBasicMaterial color="#244f72" transparent opacity={0.23} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      <mesh ref={gRef} rotation={[-Math.PI / 2, 0, 0]} position={[TX + 0.25, -5.1, TZ - 0.25]}>
        <circleGeometry args={[9.1, 96]} />
        <meshBasicMaterial color="#78b8cb" transparent opacity={0.11} blending={THREE.AdditiveBlending} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
    </group>
  );
}

/* ─── Falling brain fruits (replace vertical lines) ─── */
function Vines() {
  const fruitRefs = useRef([]);

  const drops = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 26; i++) {
      const a = Math.random() * Math.PI * 2;
      const rad = 1.1 + Math.random() * 3.9;
      arr.push({
        anchor: [Math.cos(a) * rad + TX, 2.2 + Math.random() * 3.9, Math.sin(a) * rad + TZ],
        drop: 2.3 + Math.random() * 4.1,
        speed: 0.04 + Math.random() * 0.07,
        phase: Math.random(),
        spin: 0.8 + Math.random() * 1.5,
        scale: 0.18 + Math.random() * 0.12,
      });
    }
    return arr;
  }, []);

  useFrame(({ clock, camera }) => {
    const t = clock.getElapsedTime();
    const camTarget = new THREE.Vector3(TX, 2.4, TZ);
    const camDist = camera.position.distanceTo(camTarget);
    const nearBoost = THREE.MathUtils.clamp((17 - camDist) / 10, 0, 1);

    drops.forEach((d, i) => {
      const fruit = fruitRefs.current[i];
      if (!fruit) return;

      const progress = (t * d.speed + d.phase) % 1;
      const swayX = Math.sin(t * 0.9 + i * 0.6) * 0.09;
      const swayZ = Math.cos(t * 0.8 + i * 0.55) * 0.07;
      const x = d.anchor[0] + swayX;
      const y = d.anchor[1] - d.drop * progress;
      const z = d.anchor[2] + swayZ;

      fruit.position.set(x, y, z);
      fruit.rotation.y += 0.003 * d.spin;
      fruit.rotation.z = Math.sin(t * 0.7 + i) * 0.15;

      const pulse = 0.94 + Math.sin(t * 1.35 + i * 0.5) * 0.08;
      const s = d.scale * pulse * (1 + nearBoost * 0.22);
      fruit.scale.set(s * 1.12, s * 0.84, s);

      if (fruit.material) {
        fruit.material.emissiveIntensity = 0.08 + nearBoost * 0.1 + Math.sin(t * 1.2 + i) * 0.016;
      }
    });
  });

  return (
    <group>
      {drops.map((d, i) => (
        <mesh key={`drop-brain-${i}`} ref={(el) => { fruitRefs.current[i] = el; }} position={d.anchor} geometry={brainFruitGeo}>
          <meshPhysicalMaterial
            map={brainTex}
            bumpMap={brainBump}
            bumpScale={0.22}
            roughness={0.52}
            metalness={0.01}
            clearcoat={0.36}
            clearcoatRoughness={0.36}
            transmission={0.01}
            ior={1.22}
            emissive="#78484d"
            emissiveIntensity={0.12}
          />
        </mesh>
      ))}
    </group>
  );
}

/* ─── Glowing root lines on ground ─── */
function GlowRoots() {
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    refs.current.forEach((r, i) => { if (r) r.material.opacity = 0.35 + Math.sin(t * 0.3 + i * 0.5) * 0.15; });
  });
  const roots = useMemo(() => {
    const r = [];
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const l = 2.5 + Math.random() * 5.5;
      r.push({
        p: [Math.cos(a) * l * 0.35 + TX, -5.1, Math.sin(a) * l * 0.35 + TZ],
        r: [0.05, a, Math.PI / 2],
        s: [0.04, l, 0.035],
        c: ["#39F3CC", "#2aff8a", "#44ddaa"][i % 3],
      });
    }
    return r;
  }, []);
  return (
    <group>
      {roots.map((r, i) => (
        <mesh key={i} ref={(el) => { refs.current[i] = el; }} position={r.p} rotation={r.r} scale={r.s}>
          <cylinderGeometry args={[0.5, 0.12, 1, 4]} />
          <meshBasicMaterial color={r.c} transparent opacity={0.35} blending={THREE.AdditiveBlending} />
        </mesh>
      ))}
    </group>
  );
}

/* ─── Ground plantation around roots ─── */
function GroundCover() {
  const clumpRefs = useRef([]);
  const clumps = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 180; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 1.2 + Math.pow(Math.random(), 0.66) * 10.2;
      const x = TX + Math.cos(a) * r;
      const z = TZ + Math.sin(a) * r;
      arr.push({
        p: [x, -4.98 + Math.random() * 0.15, z],
        rot: Math.random() * Math.PI * 2,
        s: 0.58 + Math.random() * 0.92,
        blades: 5 + Math.floor(Math.random() * 4),
        bush: Math.random() < 0.45,
        bushSize: 0.12 + Math.random() * 0.07,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    clumpRefs.current.forEach((g, i) => {
      if (!g || !clumps[i]) return;
      g.rotation.y = clumps[i].rot + Math.sin(t * 0.22 + clumps[i].phase) * 0.03;
      g.rotation.z = Math.sin(t * 0.45 + clumps[i].phase) * 0.03;
    });
  });

  return (
    <group>
      {clumps.map((clump, i) => (
        <group key={`plant-clump-${i}`} ref={(el) => { clumpRefs.current[i] = el; }} position={clump.p} scale={[clump.s, clump.s, clump.s]}>
          {Array.from({ length: clump.blades }).map((_, j) => {
            const offA = (j / clump.blades) * Math.PI * 2;
            const offR = 0.04 + (j % 2) * 0.03;
            return (
              <mesh
                key={`blade-${j}`}
                position={[Math.cos(offA) * offR, 0.12 + j * 0.01, Math.sin(offA) * offR]}
                rotation={[0.08 + j * 0.02, offA + (j % 2 ? 0.3 : -0.2), 0]}
              >
                <coneGeometry args={[0.035, 0.34 + (j % 2) * 0.08, 5]} />
                <meshStandardMaterial color={j % 2 ? "#5f8f47" : "#739f57"} roughness={0.92} metalness={0.01} />
              </mesh>
            );
          })}
          {clump.bush && (
            <mesh position={[0, 0.13, 0]}>
              <sphereGeometry args={[clump.bushSize, 10, 8]} />
              <meshStandardMaterial map={foliageTex} color="#84ad64" roughness={0.95} metalness={0.01} />
            </mesh>
          )}
        </group>
      ))}
    </group>
  );
}

/* ─── Stars ─── */
function Stars() {
  const ref = useRef(null);
  const geo = useMemo(() => {
    const c = 3000, pos = new Float32Array(c * 3);
    for (let i = 0; i < c; i++) {
      const i3 = i * 3, rad = 30 + Math.random() * 50;
      const th = Math.random() * Math.PI * 2, ph = Math.acos(Math.random() * 2 - 1);
      pos[i3] = rad * Math.sin(ph) * Math.cos(th);
      pos[i3 + 1] = Math.abs(rad * Math.cos(ph) * 0.55) + 2;
      pos[i3 + 2] = rad * Math.sin(ph) * Math.sin(th);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);
  useEffect(() => () => geo.dispose(), [geo]);
  useFrame(({ clock }) => { if (ref.current) ref.current.rotation.y = clock.getElapsedTime() * 0.001; });
  return (
    <points ref={ref} geometry={geo}>
      <pointsMaterial color="#8899cc" size={0.04} sizeAttenuation transparent opacity={0.7} depthWrite={false} />
    </points>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN SCENE
   ═══════════════════════════════════════════════════════════ */
export default function LandingNetwork3D() {
  return (
    <Canvas
      camera={LOOK_PRESET.camera}
      shadows
      gl={{ alpha: true, antialias: true, powerPreference: "high-performance" }}
      dpr={LOOK_PRESET.dpr}
    >
      <color attach="background" args={[LOOK_PRESET.fog.color]} />
      <fog attach="fog" args={[LOOK_PRESET.fog.color, LOOK_PRESET.fog.near, LOOK_PRESET.fog.far]} />

      <RenderQualityTuner />

      {/* OrbitControls - mouse pan/zoom/rotate */}
      <OrbitControls
        enableZoom={true}
        enablePan={true}
        enableRotate={true}
        minDistance={LOOK_PRESET.controls.minDistance}
        maxDistance={LOOK_PRESET.controls.maxDistance}
        target={LOOK_PRESET.controls.target}
        autoRotate={false}
        screenSpacePanning={true}
        zoomSpeed={LOOK_PRESET.controls.zoomSpeed}
        rotateSpeed={LOOK_PRESET.controls.rotateSpeed}
        panSpeed={LOOK_PRESET.controls.panSpeed}
        mouseButtons={{
          LEFT: THREE.MOUSE.PAN,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.ROTATE,
        }}
        enableDamping={true}
        dampingFactor={LOOK_PRESET.controls.dampingFactor}
        minPolarAngle={LOOK_PRESET.controls.minPolarAngle}
        maxPolarAngle={LOOK_PRESET.controls.maxPolarAngle}
      />

      {/* Lighting setup - natural warm daylight with depth */}
      <ambientLight intensity={0.34} color="#d7e5df" />
      <hemisphereLight intensity={0.48} groundColor="#6f5a3e" color="#d8e9f8" />
      <directionalLight
        castShadow
        position={[11, 14, 8]}
        intensity={1.34}
        color="#ffe9cb"
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={0.1}
        shadow-camera-far={60}
        shadow-camera-left={-14}
        shadow-camera-right={14}
        shadow-camera-top={14}
        shadow-camera-bottom={-14}
      />
      <directionalLight position={[-8, 7, -5]} intensity={0.38} color="#b6d6f0" />
      <pointLight position={[TX + 0.8, -4.4, TZ]} intensity={0.2} color="#7fa9be" distance={20} decay={2} />
      <pointLight position={[TX - 2.5, 4.4, TZ - 1.8]} intensity={0.22} color="#d4f6ea" distance={18} decay={2} />

      {/* Scene components */}
      <Stars />
      <SkyAtmosphere />
      <DistantMountains />
      <TreeIsland />
      <TreeTrunk />
      <BrainFruits />
      <MassiveCanopy />
      <Vines />
      <GlowRoots />
      <FlowerBushes />
      <GroundCover />
      <Water />
    </Canvas>
  );
}
